<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, parseISO } from 'date-fns';
import DetailRow from './DetailRow.vue';

const props = defineProps({
  details: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const KEY = 'CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FORM.FLIGHT';

const formatAt = value => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'dd MMM yyyy, HH:mm');
  } catch {
    return value;
  }
};

const route = computed(() => {
  const origin = props.details.origin?.locationCode;
  const destination = props.details.destination?.locationCode;
  return origin && destination ? `${origin} → ${destination}` : '';
});

const flight = computed(() =>
  [props.details.airline, props.details.flightNumber].filter(Boolean).join(' ')
);

const tripType = computed(() =>
  props.details.tripType
    ? t(`${KEY}.TRIP_TYPE_OPTIONS.${props.details.tripType.toUpperCase()}`)
    : ''
);

const cabin = computed(() =>
  props.details.cabinClass
    ? t(`${KEY}.CABIN_CLASS_OPTIONS.${props.details.cabinClass.toUpperCase()}`)
    : ''
);

const passengers = computed(() => props.details.passengers || {});

const passengerCount = computed(() =>
  [
    passengers.value.adults &&
      `${passengers.value.adults} ${t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ADULTS')}`,
    passengers.value.children &&
      `${passengers.value.children} ${t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CHILDREN')}`,
  ]
    .filter(Boolean)
    .join(', ')
);

const names = computed(() => (passengers.value.names || []).filter(Boolean));
</script>

<template>
  <div class="space-y-2">
    <p class="text-xs font-medium text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FLIGHT') }}
    </p>
    <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-2">
      <DetailRow :label="t(`${KEY}.AIRLINE`)" :value="flight" />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.ROUTE')"
        :value="route"
      />
      <DetailRow
        :label="t(`${KEY}.DEPARTURE`)"
        :value="formatAt(details.departureAt)"
      />
      <DetailRow
        :label="t(`${KEY}.RETURN`)"
        :value="formatAt(details.returnAt)"
      />
      <DetailRow :label="t(`${KEY}.TRIP_TYPE`)" :value="tripType" />
      <DetailRow :label="t(`${KEY}.CABIN_CLASS`)" :value="cabin" />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.PASSENGERS')"
        :value="passengerCount"
      />
      <DetailRow
        :label="t(`${KEY}.PASSENGER_NAMES`)"
        :value="names.join(', ')"
      />
      <DetailRow
        :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.NOTES')"
        :value="details.notes"
      />
    </div>
  </div>
</template>
